import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { deleteFile, updateFile, uploadFile } from "utils/collegeDocumentService";
import { FILES_QUERY_KEY, FOLDERS_QUERY_KEY, SEARCH_QUERY_KEY } from "./documentLibraryQueryKeys";
import { libraryErrorMessage } from "./libraryErrorMessage";

/** Upload, update (rename/move/toggle-active) and delete mutations for a single file. */
export const useFileMutations = (universityId, selectedFolderId, uploadName, onUploaded) => {
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [FOLDERS_QUERY_KEY, universityId] });
    queryClient.invalidateQueries({ queryKey: [FILES_QUERY_KEY, universityId] });
    // On-screen search hits name folders and files that just changed, so they go stale too.
    queryClient.invalidateQueries({ queryKey: [SEARCH_QUERY_KEY, universityId] });
  };

  const uploadMutation = useMutation({
    mutationFn: (file) =>
      uploadFile({ universityId, folderId: selectedFolderId, name: uploadName, file }),
    onSuccess: () => {
      toast.success("Document uploaded");
      invalidate();
      onUploaded();
    },
    onError: (error) => toast.error(libraryErrorMessage(error, "Upload failed")),
  });

  const fileMutation = useMutation({
    mutationFn: ({ fileId, payload }) => updateFile(fileId, payload),
    onSuccess: () => {
      toast.success("Document updated");
      invalidate();
    },
    onError: (error) => toast.error(libraryErrorMessage(error, "Could not update the document")),
  });

  const deleteFileMutation = useMutation({
    mutationFn: (fileId) => deleteFile(fileId),
    onSuccess: () => {
      toast.success("Document deleted");
      invalidate();
    },
    onError: (error) => toast.error(libraryErrorMessage(error, "Could not delete the document")),
  });

  return { uploadMutation, fileMutation, deleteFileMutation };
};
